import type { FeatureData, SimilarCase } from "@/types/spectrum";
import { groupFeaturesByObservation } from "@/utils/observationGrouping";

interface SecondaryInsightsPeekProps {
  similarCases: SimilarCase[];
  features: FeatureData[];
}

/**
 * SecondaryInsightsPeek — Row 3 전 1줄 보조 분석 preview (v2 spec §3, K5/K8).
 *
 * 콘텐츠 3종 고정 (K8):
 * 1. 유사 사건 n건 (n = similarCases.length, ≥3건 강조)
 * 2. 우세 물질 1개 (features.area 최대값 기준)
 * 3. 최저 JSD 거리 화합물 1개
 */
export default function SecondaryInsightsPeek({
  similarCases,
  features,
}: SecondaryInsightsPeekProps) {
  // 1. 유사 사건 수
  const caseCount = similarCases.length;
  const caseHighlight = caseCount >= 3;

  // 2. 우세 물질 — 신호(area) 최대 **관찰**의 1순위 후보 (2026-08-31).
  // 이전에는 행 단위 area 최대였는데 그 area 가 점수×10000 가짜값이라
  // "우세 물질(면적 N)"이 사실상 점수 1등이었다. 이제 area 는 관찰의 실제
  // 신호 합이고, 같은 관찰의 후보들은 값이 같으므로 관찰 단위로 고른 뒤
  // 1순위 후보를 대표로 쓴다. 신호가 전부 미상(0)이면 우세를 말하지 않는다.
  const observations = groupFeaturesByObservation(features);
  const dominantObservation =
    observations.length > 0
      ? observations.reduce((prev, cur) =>
          (cur.features[0]?.area ?? 0) > (prev.features[0]?.area ?? 0)
            ? cur
            : prev,
        )
      : null;
  const dominantFeature =
    dominantObservation && (dominantObservation.features[0]?.area ?? 0) > 0
      ? dominantObservation.features[0]
      : null;

  // 3. 최저 JSD 거리 화합물
  const featuresWithJSD = features.filter((f) => f.jsd_distance != null);
  const lowestJSDFeature =
    featuresWithJSD.length > 0
      ? featuresWithJSD.reduce((prev, cur) =>
          // biome-ignore lint/style/noNonNullAssertion: filtered above
          cur.jsd_distance! < prev.jsd_distance! ? cur : prev,
        )
      : null;

  return (
    <div
      data-testid="secondary-insights-peek"
      className="bg-blue-50/60 border border-blue-200/70 rounded-xl px-4 py-2"
    >
      <div className="flex items-center gap-6 flex-wrap text-sm">
        {/* Section label */}
        <span className="text-xs font-bold text-blue-600 uppercase tracking-wide whitespace-nowrap">
          보조 분석 미리보기
        </span>

        <div className="h-4 border-l border-blue-300 hidden sm:block" />

        {/* 1. 유사 사건 n건 */}
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="text-gray-600 text-xs">유사 사건</span>
          <span
            className={`font-bold text-sm ${
              caseHighlight ? "text-orange-700" : "text-gray-800"
            }`}
          >
            {caseCount}건
          </span>
          {caseHighlight && (
            <span className="text-xs text-orange-600 font-medium">(주의)</span>
          )}
        </div>

        <div className="h-4 border-l border-blue-200 hidden sm:block" />

        {/* 2. 우세 물질 */}
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="text-gray-600 text-xs">우세 물질</span>
          <span className="font-bold text-sm text-gray-800">
            {dominantFeature
              ? dominantFeature.predicted_substance
              : features.length > 0
                ? "신호 미상"
                : "없음"}
          </span>
          {dominantFeature && (
            <span className="text-xs text-gray-500">
              (관찰 신호 {Math.round(dominantFeature.area).toLocaleString()})
            </span>
          )}
        </div>

        <div className="h-4 border-l border-blue-200 hidden sm:block" />

        {/* 3. 최저 JSD 거리 — 2026-08-28 (P2-3): 초록색 강조를 중립색으로.
            거리 최솟값은 '좋음'이 아니다. 경계값 0.2/0.5 가 우리가 임의로 정한
            값이라 2026-08-27 에 해석 라벨과 구간 색을 보류했는데
            (SHOW_JSD_LABEL, FeaturesTable.getJsdColor), 이 컴포넌트만 빠져
            색으로 같은 해석을 주고 있었다. 값 자체는 그대로 보여준다. */}
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          <span
            className="text-gray-600 text-xs"
            title="JSD 거리가 가장 작은 후보. 거리 순위일 뿐 정답 확률이 아닙니다."
          >
            최저 JSD
          </span>
          {lowestJSDFeature ? (
            <>
              <span className="font-bold text-sm text-gray-800">
                {lowestJSDFeature.predicted_substance}
              </span>
              <span className="text-xs font-mono text-gray-600">
                ({lowestJSDFeature.jsd_distance?.toFixed(3)})
              </span>
            </>
          ) : (
            <span className="text-sm text-gray-400">없음</span>
          )}
        </div>
      </div>
    </div>
  );
}
